<template>
  <article class="page-search" :lang="zh ? 'zh-Hant' : 'en'">
    <header class="search-head">
      <p class="search-kicker">CHE-YU WU / SEARCH</p>
      <h1>{{ t.title }}</h1>
      <p class="search-intro">{{ t.intro }}</p>
      <form class="search-box" role="search" @submit.prevent="submit">
        <label class="visually-hidden" for="site-search-input">{{ t.label }}</label>
        <input
          id="site-search-input"
          ref="inputEl"
          v-model="draft"
          type="search"
          :placeholder="t.placeholder"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          enterkeyhint="search"
          @focus="warm"
        />
        <button type="submit">{{ t.submit }}</button>
      </form>
      <p class="search-status" role="status" aria-live="polite">{{ statusText }}</p>
    </header>

    <div v-if="loadError" class="search-error" role="alert">
      <p>{{ t.error }}</p>
      <button type="button" @click="load">{{ t.retry }}</button>
    </div>

    <section v-if="landing" class="search-landing">
      <div class="search-block">
        <h2>{{ t.tryTitle }}</h2>
        <div class="search-examples">
          <a v-for="ex in examples" :key="ex" :href="searchUrl({ q: ex })" @click.prevent="goQuery(ex)">{{ ex }}</a>
        </div>
      </div>
      <div class="search-block">
        <h2>{{ t.categories }}</h2>
        <ul class="search-cats">
          <li v-for="kind in KIND_ORDER" :key="kind">
            <a :href="searchUrl({ type: kind })" @click.prevent="go({ q: '', type: kind, sort: '', year: '' })">
              <span>{{ kindName(kind) }}</span><span v-if="meta" class="search-cat-n">{{ meta.counts?.[kind] ?? 0 }}</span>
            </a>
          </li>
        </ul>
      </div>
      <div v-if="featured.length" class="search-block">
        <h2>{{ t.featured }}</h2>
        <ul class="search-featured">
          <li v-for="doc in featured" :key="doc.i">
            <a :href="doc.u" @click="onLink($event, { doc }, 0, 'page')">
              <img v-if="doc.img" :src="doc.img" alt="" loading="lazy" decoding="async" width="160" height="90" />
              <span class="search-featured-t">{{ doc.t }}</span>
              <span class="search-muted">{{ [kindItem(doc.k), doc.y].filter(Boolean).join(' · ') }}</span>
            </a>
          </li>
        </ul>
      </div>
    </section>

    <div v-else class="search-layout">
      <aside class="search-facets" :aria-label="t.filters">
        <div class="facet">
          <h2>{{ t.type }}</h2>
          <ul>
            <li v-for="kind in kindsPresent" :key="kind">
              <button type="button" :aria-pressed="types.has(kind)" :class="{ on: types.has(kind) }" @click="toggleType(kind)">
                <span>{{ kindName(kind) }}</span><span class="n">{{ kindCounts[kind] }}</span>
              </button>
            </li>
          </ul>
          <button v-if="types.size" type="button" class="facet-clear" @click="go({ type: '' })">{{ t.clearType }}</button>
        </div>
        <div class="facet" v-if="yearOptions.length">
          <label for="search-year"><h2>{{ t.year }}</h2></label>
          <select id="search-year" :value="year" @change="go({ year: $event.target.value })">
            <option value="">{{ t.allYears }}</option>
            <option v-for="y in yearOptions" :key="y.year" :value="y.year">{{ y.year }}（{{ y.n }}）</option>
          </select>
        </div>
        <div class="facet">
          <label for="search-sort"><h2>{{ t.sort }}</h2></label>
          <select id="search-sort" :value="sortMode" @change="go({ sort: $event.target.value })">
            <option v-for="s in SORTS" :key="s" :value="s" :disabled="s === 'relevance' && !q">{{ t.sorts[s] }}</option>
          </select>
        </div>
      </aside>

      <div class="search-results">
        <p v-if="!engine && !loadError" class="search-muted">{{ t.loading }}</p>
        <template v-else-if="engine">
          <p v-if="partialNote" class="search-note">{{ partialNote }}</p>
          <ol v-if="shown.length" class="result-list">
            <li v-for="(row, i) in shown" :key="row.doc.i" class="result" :class="['k-' + row.doc.k, { 'no-thumb': !row.doc.img }]">
              <span v-if="!row.doc.img" class="result-thumb result-glyph" aria-hidden="true"><svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" focusable="false" v-html="kindIcon(row.doc.k)"></svg></span>
              <a v-if="row.doc.img" class="result-thumb" :class="thumbShape(row.doc.k)" :href="row.doc.u" tabindex="-1" aria-hidden="true" :target="external(row.doc.u) ? '_blank' : undefined" :rel="external(row.doc.u) ? 'noopener noreferrer' : undefined" @click="onLink($event, row, i)">
                <img :src="row.doc.img" alt="" loading="lazy" decoding="async" />
              </a>
              <div class="result-body">
                <p class="result-meta">
                  <span class="tag result-kind" :class="'k-' + row.doc.k">{{ kindItem(row.doc.k) }}</span>
                  <span v-if="row.doc.y">{{ row.doc.y }}</span>
                  <span v-if="row.doc.l !== locale" class="result-lang">{{ row.doc.l === 'zh' ? t.onlyZh : t.onlyEn }}</span>
                  <span v-if="external(row.doc.u)" class="result-ext">{{ t.external }}</span>
                </p>
                <h3><a :href="row.doc.u" :target="external(row.doc.u) ? '_blank' : undefined" :rel="external(row.doc.u) ? 'noopener noreferrer' : undefined" @click="onLink($event, row, i)" v-html="row.titleHtml"></a></h3>
                <p class="result-snippet" v-html="row.snippet"></p>
                <button type="button" class="result-more" :aria-expanded="expanded.has(row.doc.i)" @click="toggleRelated(row.doc)">
                  {{ expanded.has(row.doc.i) ? t.hideRelated : t.showRelated }}
                </button>
                <ul v-if="expanded.has(row.doc.i)" class="result-related">
                  <li v-for="n in relatedOfDoc(row.doc)" :key="n.doc.i">
                    <a :href="n.doc.u" :target="external(n.doc.u) ? '_blank' : undefined" :rel="external(n.doc.u) ? 'noopener noreferrer' : undefined" @click="onLink($event, n, i)">{{ n.doc.t }}</a>
                    <span class="search-muted">{{ kindItem(n.doc.k) }}</span>
                  </li>
                  <li v-if="!relatedOfDoc(row.doc).length" class="search-muted">{{ t.noRelated }}</li>
                </ul>
              </div>
            </li>
          </ol>
          <div v-else class="search-empty">
            <p>{{ t.none(q) }}</p>
            <div v-if="suggestions.length" class="search-examples">
              <span class="search-muted">{{ t.tryShorter }}</span>
              <a v-for="w in suggestions" :key="w" :href="searchUrl({ q: w })" @click.prevent="goQuery(w)">{{ w }}</a>
            </div>
          </div>
          <button v-if="remaining > 0" type="button" class="search-more" @click="limit += PAGE">{{ t.more(Math.min(PAGE, remaining)) }}</button>

          <section v-if="relatedList.length" class="search-related" aria-labelledby="related-title">
            <h2 id="related-title">{{ t.relatedTitle }}</h2>
            <p class="search-muted">{{ relatedNote }}</p>
            <ul>
              <li v-for="n in relatedList" :key="n.doc.i">
                <a :href="n.doc.u" :target="external(n.doc.u) ? '_blank' : undefined" :rel="external(n.doc.u) ? 'noopener noreferrer' : undefined" @click="onLink($event, n, 0)">
                  <img v-if="n.doc.img" :src="n.doc.img" alt="" loading="lazy" decoding="async" width="72" height="72" />
                  <span class="rel-text">
                    <span class="rel-title">{{ n.doc.t }}</span>
                    <span class="search-muted">{{ [kindItem(n.doc.k), n.doc.y].filter(Boolean).join(' · ') }}</span>
                    <span class="rel-via">{{ t.via(n.via.t) }}</span>
                  </span>
                </a>
              </li>
            </ul>
          </section>
        </template>
      </div>
    </div>
  </article>
</template>

<script setup>
import { ref, shallowRef, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { KIND_ORDER, kindLabel, kindItemLabel, kindIcon } from '@/search/kinds.mjs'
import { countByKind } from '@/search/engine.mjs'
import { tokenize } from '@/search/tokenizer.mjs'
import { relatedForResults, nearestByTitle, relatedOf } from '@/search/related.mjs'
import { loadEngine, loadRelated, loadMeta, trackSearch, trackSelect, isExternal } from '@/search/client.mjs'
import featuredConfig from '@/data/home-featured.json'

const route = useRoute(), router = useRouter()
const { locale: i18nLocale } = useI18n()
const zh = computed(() => i18nLocale.value === 'zh')
const locale = computed(() => (zh.value ? 'zh' : 'en'))
const PAGE = 20, SORTS = ['relevance', 'newest', 'oldest']
const external = isExternal

const T = {
  zh: {
    title: '搜尋', intro: '作品、展覽、對話錄、文章、報導與音樂都在這裡，也能找到意思相近的內容。',
    label: '搜尋全站內容', placeholder: '輸入想找的詞，例如靈魂魚、聲音、TouchDesigner', submit: '搜尋',
    tryTitle: '試試看', categories: '依類型瀏覽', featured: '精選', loading: '索引載入中…',
    error: '搜尋索引載入失敗，請稍後再試。', retry: '重新載入',
    filters: '篩選與排序', type: '類型', clearType: '清除類型', year: '年份', allYears: '全部年份', sort: '排序',
    sorts: { relevance: '相關度', newest: '最新優先', oldest: '最舊優先' },
    onlyZh: '僅有中文版', onlyEn: '英文版', external: '外部連結',
    showRelated: '相關 3 筆', hideRelated: '收合相關', noRelated: '沒有找到相近的內容。',
    none: q => (q ? `找不到「${q}」的結果。` : '這個類型目前沒有內容。'), tryShorter: '試試較短的詞：',
    more: n => `再看 ${n} 筆`, relatedTitle: '意思相近的內容',
    via: x => `與《${x}》相近`,
    status: (n, q) => (q ? `「${q}」共 ${n} 筆結果` : `共 ${n} 筆`),
    approx: x => `找不到字面符合的內容。以下是與《${x}》最接近的內容，只能算近似。`,
    partial: '沒有同時包含所有詞的內容，以下是符合部分詞的結果。',
    relNote: '依前幾筆結果的語意相似度推算，不一定含有你輸入的詞。',
  },
  en: {
    title: 'Search', intro: 'Works, exhibitions, dialogues, essays, press and music in one place, with related pieces nearby.',
    label: 'Search the whole site', placeholder: 'Type a word, such as SoulFish, sound, TouchDesigner', submit: 'Search',
    tryTitle: 'Try these', categories: 'Browse by type', featured: 'Featured', loading: 'Loading the index…',
    error: 'The search index did not load. Please try again.', retry: 'Reload',
    filters: 'Filters and sorting', type: 'Type', clearType: 'Clear type', year: 'Year', allYears: 'All years', sort: 'Sort',
    sorts: { relevance: 'Relevance', newest: 'Newest first', oldest: 'Oldest first' },
    onlyZh: 'Chinese only', onlyEn: 'English', external: 'External link',
    showRelated: '3 related', hideRelated: 'Hide related', noRelated: 'No related pieces found.',
    none: q => (q ? `No results for “${q}”.` : 'Nothing in this type yet.'), tryShorter: 'Try a shorter word: ',
    more: n => `Show ${n} more`, relatedTitle: 'Related pieces',
    via: x => `Close to “${x}”`,
    status: (n, q) => (q ? `${n} ${n === 1 ? 'result' : 'results'} for “${q}”` : `${n} items`),
    approx: x => `Nothing matches those words literally. These are closest to “${x}”, so treat them as approximate.`,
    partial: 'Nothing contains every word. These match some of them.',
    relNote: 'Picked by semantic similarity to the top results; they may not contain your words.',
  },
}
const t = computed(() => T[locale.value])
const kindName = kind => kindLabel(kind, locale.value)
const kindItem = kind => kindItemLabel(kind, locale.value)
const EXAMPLES = { zh: ['靈魂魚', '萬物公式', '生成藝術', '聲音', 'TouchDesigner', 'Muse'], en: ['SoulFish', 'The Great Equation', 'generative art', 'sound', 'TouchDesigner', 'Muse'] }
const examples = computed(() => EXAMPLES[locale.value])

// 狀態都在網址：?q= &type= &sort= &year=
const str = v => (typeof v === 'string' ? v : Array.isArray(v) ? v[0] || '' : '')
const q = computed(() => str(route.query.q).trim())
const types = computed(() => new Set(str(route.query.type).split(',').filter(k => KIND_ORDER.includes(k))))
const year = computed(() => (/^\d{4}$/.test(str(route.query.year)) ? str(route.query.year) : ''))
const sortMode = computed(() => {
  const s = str(route.query.sort)
  if (SORTS.includes(s) && !(s === 'relevance' && !q.value)) return s
  return q.value ? 'relevance' : 'newest'
})
const landing = computed(() => !q.value && !types.value.size)
const searchUrl = (patch) => {
  const params = String(new URLSearchParams(Object.entries(patch).filter(([, v]) => v)))
  return route.path + (params ? '?' + params : '')
}
function go(patch, method = 'push') {
  const query = { ...route.query, ...patch }
  for (const k of Object.keys(query)) if (!query[k]) delete query[k]
  router[method]({ path: route.path, query })
}
const goQuery = term => { trackSearch(term); go({ q: term }) }
function toggleType(kind) {
  const next = new Set(types.value)
  next.has(kind) ? next.delete(kind) : next.add(kind)
  go({ type: [...next].join(','), year: '' })
}

// 輸入框：打字用 replace、Enter 與換篩選用 push
const draft = ref(q.value)
const inputEl = ref(null)
let typingTimer, idleTimer
watch(q, v => { if (v !== draft.value.trim()) draft.value = v })
watch(draft, v => {
  clearTimeout(typingTimer); clearTimeout(idleTimer)
  typingTimer = setTimeout(() => { if (v.trim() !== q.value) go({ q: v.trim() }, 'replace') }, 220)
  if (v.trim()) idleTimer = setTimeout(() => trackSearch(v.trim()), 1500)
})
function submit() {
  clearTimeout(typingTimer)
  const v = draft.value.trim()
  if (v) trackSearch(v)
  if (v !== q.value) go({ q: v })
}

// 資料：進頁面後才載入索引（有 q 或 type 要完整索引；首屏只要精簡索引與筆數）
const engine = shallowRef(null), meta = shallowRef(null), rel = shallowRef(null)
const loadError = ref(false)
const needFull = computed(() => !landing.value)
let loadToken = 0
async function load() {
  const token = ++loadToken
  loadError.value = false
  try {
    const [eng, m] = await Promise.all([loadEngine(needFull.value ? 'full' : 'lite', locale.value), loadMeta()])
    if (token !== loadToken) return
    engine.value = eng; meta.value = m
    loadRelated().then(r => { if (token === loadToken) rel.value = r })
  } catch { if (token === loadToken && needFull.value) loadError.value = true }   // 首屏（精簡索引）失敗不報錯，預渲染時這些檔還不存在
}
const warm = () => { loadEngine('full', locale.value).catch(() => {}) }
watch([needFull, locale], () => { engine.value = null; load() })
onMounted(load)
onBeforeUnmount(() => { clearTimeout(typingTimer); clearTimeout(idleTimer); loadToken++ })

// 結果
const limit = ref(PAGE)
const nearest = computed(() => (engine.value && q.value ? nearestByTitle(q.value, engine.value.docs, tokenize) : null))
const rankedAll = computed(() => {
  const eng = engine.value
  if (!eng || landing.value) return []
  if (q.value) return eng.rank(q.value, { lang: locale.value })
  return eng.docs.filter(d => types.value.has(d.k)).map(doc => ({ doc, score: 0 }))
})
const kindCounts = computed(() => countByKind(rankedAll.value))
const kindsPresent = computed(() => KIND_ORDER.filter(k => kindCounts.value[k] || types.value.has(k)))
const byType = computed(() => (types.value.size ? rankedAll.value.filter(r => types.value.has(r.doc.k)) : rankedAll.value))
const yearOptions = computed(() => {
  const counts = new Map()
  for (const r of byType.value) if (r.doc.y) counts.set(r.doc.y, (counts.get(r.doc.y) || 0) + 1)
  return [...counts].map(([y, n]) => ({ year: y, n })).sort((a, b) => b.year.localeCompare(a.year))
})
const filtered = computed(() => {
  let list = year.value ? byType.value.filter(r => r.doc.y === year.value) : byType.value
  if (sortMode.value !== 'relevance') {
    const dir = sortMode.value === 'newest' ? -1 : 1
    list = [...list].sort((a, b) => {
      if (!a.doc.y !== !b.doc.y) return a.doc.y ? -1 : 1   // 沒有年份的排最後
      return (a.doc.y.localeCompare(b.doc.y) * dir) || (b.score - a.score) || a.doc.i.localeCompare(b.doc.i)
    })
  }
  return list
})
watch([q, types, year, sortMode], () => { limit.value = PAGE; expanded.value = new Set() })
const shown = computed(() => (engine.value ? engine.value.decorate(filtered.value.slice(0, limit.value), q.value) : []))
const remaining = computed(() => filtered.value.length - limit.value)
const suggestions = computed(() => (engine.value && q.value && !filtered.value.length ? engine.value.suggest(q.value) : []))
const partialNote = computed(() => {
  if (!engine.value || !q.value) return ''
  if (!rankedAll.value.length && nearest.value) return t.value.approx(nearest.value.t)
  return rankedAll.value[0]?.partial ? t.value.partial : ''
})
const statusText = computed(() => {
  if (landing.value) return ''
  if (!engine.value) return loadError.value ? '' : t.value.loading
  return t.value.status(filtered.value.length, q.value)
})

// 語意相近：前 5 筆結果的鄰居，依（名次權重 × 相似度）加總；查無字面結果時改用標題最接近那筆的鄰居
const relatedList = computed(() => {
  if (!engine.value || !rel.value || landing.value) return []
  const byId = engine.value.byId
  if (filtered.value.length) return relatedForResults(filtered.value, rel.value, byId, { topN: 5, limit: 8 })
  const near = nearest.value
  if (!near) return []
  return rel.value.of(near.i).filter(n => byId.has(n.key)).slice(0, 8).map(n => ({ doc: byId.get(n.key), score: n.score, via: near }))
})
const relatedNote = computed(() => (!filtered.value.length && nearest.value ? t.value.approx(nearest.value.t) : t.value.relNote))
const expanded = ref(new Set())
function toggleRelated(doc) {
  const next = new Set(expanded.value)
  next.has(doc.i) ? next.delete(doc.i) : next.add(doc.i)
  expanded.value = next
}
const relatedOfDoc = doc => (engine.value && rel.value ? relatedOf(doc, rel.value, engine.value.byId, 3) : [])

// 首屏精選：首頁精選作品前 6 件＋最新 3 篇對話錄
const featured = computed(() => {
  const eng = engine.value
  if (!eng || !landing.value) return []
  const works = (featuredConfig.slugs || []).map(s => eng.byId.get(`/work/${s}/`)).filter(Boolean).slice(0, 6)
  const dialogues = eng.docs.filter(d => d.k === 'dialogue').sort((a, b) => b.i.localeCompare(a.i)).slice(0, 3)
  return [...works, ...dialogues]
})
const thumbShape = kind => (['work', 'exhibition', 'td', 'project'].includes(kind) ? 'wide' : kind === 'sketch' ? 'square' : 'small')

function onLink(event, row, index, surface = 'page') {
  const doc = row.doc
  if (q.value) trackSearch(q.value)
  trackSelect({ kind: doc.k, id: doc.i, surface, position: index + 1 })
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || external(doc.u)) return
  event.preventDefault()
  router.push(doc.u)
}
</script>

<style scoped>
.page-search { max-width: var(--editorial-w); margin: 0 auto; padding: 56px 16px 0; text-align: left; min-height: 80vh; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.search-kicker { margin: 0 0 12px; font: 500 0.78rem var(--font-body); letter-spacing: 0.18em; color: var(--ink-3); }
h1 { font-family: var(--font-display); font-size: clamp(2.2rem, 5vw, 3.6rem); font-weight: 600; margin: 0 0 12px; }
.search-intro { max-width: 46em; margin: 0 0 28px; color: var(--ink-2); line-height: 1.8; }
.search-box { display: flex; gap: 10px; max-width: 860px; }
.search-box input { flex: 1; min-width: 0; background: var(--bg2); border: 0; border-radius: var(--r-pill); color: var(--ink); padding: 14px 22px; font: 400 1.15rem var(--font-body); outline: none; -webkit-appearance: none; appearance: none; transition: var(--t-hover); }
.search-box input:focus { box-shadow: 0 0 0 1px var(--gold); }
.search-box input::placeholder { color: var(--ink-3); }
.search-box button { flex: none; min-height: 48px; padding: 0 26px; border-radius: var(--r-pill); border: 0; background: var(--tint-2); color: var(--ink); font: 500 0.95rem var(--font-body); cursor: pointer; transition: var(--t-hover); }
.search-box button:hover { background: var(--tint-3); color: var(--gold); }
.search-status { min-height: 1.6em; margin: 14px 4px 24px; color: var(--ink-3); font-size: 0.9rem; }
.search-muted { color: var(--ink-3); font-size: 0.84rem; }
.search-error { padding: 16px 0; color: var(--ink-2); }
.search-error button, .search-more { background: var(--tint-2); border: 0; color: var(--ink); padding: 12px 24px; border-radius: var(--r-pill); cursor: pointer; font: 500 0.92rem var(--font-body); transition: var(--t-hover); }
.search-error button:hover, .search-more:hover { background: var(--tint-3); color: var(--gold); }
h2 { font: 600 0.78rem var(--font-body); letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); margin: 0 0 14px; }

.search-landing { display: grid; gap: 44px; padding-top: 8px; }
.search-examples { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.search-examples a { padding: 8px 16px; border: 0; background: var(--tint-2); border-radius: var(--r-pill); color: var(--ink-2); text-decoration: none; min-height: 40px; display: inline-flex; align-items: center; transition: var(--t-hover); }
.search-examples a:hover { color: var(--gold); background: var(--tint-3); text-decoration: none; }
.search-cats { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; }
.search-cats a { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 16px 18px; border: 0; background: var(--bg1); border-radius: var(--r-md); color: var(--ink); text-decoration: none; transition: var(--t-hover); }
.search-cats a:hover { background: var(--bg2); color: var(--gold); text-decoration: none; }
.search-cat-n { color: var(--ink-3); font-variant-numeric: tabular-nums; font-size: 0.9rem; }
.search-featured { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 18px; }
.search-featured a { display: flex; flex-direction: column; gap: 6px; color: var(--ink); text-decoration: none; }
.search-featured img { width: 100%; aspect-ratio: 16 / 9; object-fit: cover; border-radius: 8px; background: var(--bg2); }
.search-featured-t { font-weight: 500; line-height: 1.4; }

.search-layout { display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 48px; align-items: start; }
.search-facets { position: sticky; top: 96px; display: grid; gap: 28px; }
.facet ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
.facet button { width: 100%; display: flex; justify-content: space-between; gap: 10px; background: none; border: 0; border-radius: 6px; padding: 8px 10px; color: var(--ink-2); font: 400 0.95rem var(--font-body); text-align: left; cursor: pointer; transition: var(--t-hover); }
.facet button:hover { color: var(--ink); background: var(--tint-2); }
.facet button.on { color: var(--ink); background: var(--gold-tint); }
.facet button.on .n { color: var(--gold); }
.facet .n { color: var(--ink-3); font-variant-numeric: tabular-nums; }
.facet .facet-clear { margin-top: 8px; color: var(--ink-3); font-size: 0.82rem; text-decoration: underline; text-underline-offset: 3px; justify-content: flex-start; }
.facet select { width: 100%; background: var(--bg2); color: var(--ink); border: 0; border-radius: var(--r-md); padding: 10px 14px; font: 400 0.95rem var(--font-body); }
.facet label h2 { margin: 0 0 10px; }

.search-note { margin: 0 0 20px; padding: 12px 18px; border-radius: var(--r-md); color: var(--ink-2); background: var(--tint-1); font-size: 0.92rem; line-height: 1.7; }
.result-list { list-style: none; margin: 0; padding: 0; }
.result { display: grid; grid-template-columns: 176px minmax(0, 1fr); gap: 22px; padding: 22px 0; border-top: 1px solid var(--line); }
.result:first-child { border-top: 0; padding-top: 0; }
.result-thumb { display: block; border-radius: 8px; overflow: hidden; background: var(--bg2); flex: none; }
.result-thumb.wide { width: 176px; aspect-ratio: 16 / 9; }
.result-thumb.square { width: 96px; aspect-ratio: 1; }
.result-thumb.small { width: 64px; aspect-ratio: 1; }
.result-thumb.wide, .result-thumb.square, .result-thumb.small, .result-glyph { width: 176px; aspect-ratio: 16 / 9; }
.result-glyph { display: flex; align-items: center; justify-content: center; color: var(--ink-3); border: 0; background: var(--bg1); }
.result-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.result-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; margin: 0 0 6px; font-size: 0.78rem; color: var(--ink-3); font-variant-numeric: tabular-nums; }
.result-kind { letter-spacing: 0.06em; text-transform: none; font-size: 0.78rem; }
.result-lang, .result-ext { color: var(--ink-2); background: var(--tint-2); border-radius: var(--r-pill); padding: 0 9px; }
.result h3 { margin: 0 0 6px; font: 500 1.2rem/1.4 var(--font-display); }
.result h3 a { color: var(--ink); text-decoration: none; }
.result h3 a:hover { color: var(--gold); }
.result :deep(mark) { background: transparent; color: var(--gold); font-weight: 600; padding: 0; }
.result-snippet { margin: 0 0 10px; color: var(--ink-2); font-size: 0.92rem; line-height: 1.75; overflow-wrap: anywhere; }
.result-more { background: none; border: 0; padding: 6px 0; min-height: 32px; color: var(--ink-3); font: 400 0.82rem var(--font-body); cursor: pointer; text-decoration: underline; text-underline-offset: 3px; transition: var(--t-hover); }
.result-more:hover { color: var(--gold); }
.result-related { list-style: none; margin: 8px 0 0; padding: 12px 18px; border-radius: var(--r-md); background: var(--tint-1); display: grid; gap: 6px; }
.result-related a { color: var(--ink-2); }
.result-related a:hover { color: var(--gold); }
.result-related .search-muted { margin-left: 10px; }
.search-empty { padding: 20px 0; color: var(--ink-2); }
.search-more { display: block; margin: 36px auto 0; }

.search-related { margin-top: 64px; padding-top: 32px; border-top: 1px solid var(--line-2); }
.search-related ul { list-style: none; margin: 18px 0 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px 28px; }
.search-related a { display: flex; gap: 14px; align-items: flex-start; color: var(--ink); text-decoration: none; }
.search-related img { flex: none; width: 72px; height: 72px; object-fit: cover; border-radius: 6px; background: var(--bg2); }
.rel-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.rel-title { font-weight: 500; line-height: 1.4; }
.search-related a:hover .rel-title { color: var(--gold); }
.rel-via { font-size: 0.78rem; color: var(--ink-3); }

@media (max-width: 991px) {
  .search-layout { grid-template-columns: 1fr; gap: 28px; }
  .search-facets { position: static; grid-template-columns: 1fr 1fr; }
  .search-facets .facet:first-child { grid-column: 1 / -1; }
  .facet:first-child ul { display: flex; flex-wrap: wrap; gap: 8px; }
  .facet:first-child button { width: auto; border: 0; background: var(--tint-2); border-radius: var(--r-pill); padding: 7px 14px; gap: 8px; min-height: 40px; }
  .facet:first-child button.on { color: #000; background: var(--ink); }
  .facet:first-child button.on .n { color: #000; }
}
@media (max-width: 575px) {
  .page-search { padding-top: 32px; }
  .search-box { flex-direction: column; }
  .search-box button { min-height: 46px; }
  .result { grid-template-columns: 56px minmax(0, 1fr); gap: 14px; }
  .result-thumb.wide, .result-thumb.small, .result-thumb.square, .result-glyph { width: 56px; aspect-ratio: 1; }
  .search-facets { grid-template-columns: 1fr; }
}
</style>
